import { Injectable, inject } from '@angular/core';
import { Firestore, doc, docData, getDoc, collection, collectionData, query, orderBy, where } from '@angular/fire/firestore';
import { Storage, ref, getDownloadURL } from '@angular/fire/storage';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Observable, from, of, switchMap, map } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class UserPortalService {
  private firestore = inject(Firestore);
  private storage = inject(Storage);
  private functions = inject(Functions, { optional: true });

  /**
   * Fetches the session Firestore document.
   * Tries the new date-nested path first:
   *   devices/{machineId}/sessions/{date}/sessions/{sessionId}
   * If the document doesn't exist (legacy QR code with no date param, or old data),
   * falls back to the flat legacy path:
   *   devices/{machineId}/sessions/{sessionId}
   */
  getSessionMedia(machineId: string, date: string | null, sessionId: string): Observable<any> {
    if (date) {
      // Try new nested path, fall back to legacy if doc is empty
      const newDocRef = doc(this.firestore, 'devices', machineId, 'sessions', date, 'sessions', sessionId);
      return from(getDoc(newDocRef)).pipe(
        switchMap(snap => {
          if (snap.exists()) {
            // New path found â€” stream live updates from new doc
            return docData(newDocRef);
          } else {
            // Fall back to legacy flat path
            const legacyDocRef = doc(this.firestore, 'devices', machineId, 'sessions', sessionId);
            return docData(legacyDocRef);
          }
        })
      );
    } else {
      // No date param (very old QR code) â€” go straight to legacy path
      const legacyDocRef = doc(this.firestore, 'devices', machineId, 'sessions', sessionId);
      return docData(legacyDocRef);
    }
  }

  /**
   * Fetches a signed download URL from Firebase Storage.
   * Tries the new date-nested path first:
   *   Photobooth/{machineId}/{date}/{sessionId}/{filename}
   * If not found (storage/object-not-found), falls back to the legacy path:
   *   Photobooth/{machineId}/Sessions/{sessionId}/{filename}
   */
  async getFirebaseDownloadUrl(machineId: string, date: string | null, sessionId: string, filename: string): Promise<string> {
    if (date) {
      try {
        const newRef = ref(this.storage, `Photobooth/${machineId}/${date}/${sessionId}/${filename}`);
        return await getDownloadURL(newRef);
      } catch (err: any) {
        // If file not found in new path, try legacy location
        if (err?.code === 'storage/object-not-found' || err?.code === 'storage/unknown') {
          const legacyRef = ref(this.storage, `Photobooth/${machineId}/Sessions/${sessionId}/${filename}`);
          return await getDownloadURL(legacyRef);
        }
        throw err;
      }
    } else {
      // No date â€” go straight to legacy path
      const legacyRef = ref(this.storage, `Photobooth/${machineId}/Sessions/${sessionId}/${filename}`);
      return await getDownloadURL(legacyRef);
    }
  }

  /**
   * Claims a session by adding the user's email and saving it to their gallery.
   * Resolves whichever Firestore path (new or legacy) actually holds this session.
   */
  async claimSession(machineId: string, date: string | null, sessionId: string, email: string, uid: string, printUrl: string | null, gifUrl: string | null, cutStyle: string | null): Promise<void> {
    const { updateDoc, arrayUnion, setDoc, serverTimestamp } = await import('@angular/fire/firestore');

    // Resolve which Firestore path actually holds this session
    let sessionDocRef = doc(this.firestore, 'devices', machineId, 'sessions', sessionId); // default: legacy
    if (date) {
      const newDocRef = doc(this.firestore, 'devices', machineId, 'sessions', date, 'sessions', sessionId);
      const snap = await getDoc(newDocRef);
      if (snap.exists()) {
        sessionDocRef = newDocRef;
      }
    }

    await updateDoc(sessionDocRef, {
      claimedByEmails: arrayUnion(email)
    });

    const galleryDocRef = doc(this.firestore, 'users', uid, 'my_gallery', sessionId);
    await setDoc(galleryDocRef, {
      sessionId,
      machineId,
      date,
      printUrl,
      gifUrl,
      cutStyle,
      savedAt: serverTimestamp()
    });
  }

  getGallery(uid: string): Observable<any[]> {
    const galleryCollection = collection(this.firestore, 'users', uid, 'my_gallery');
    const q = query(galleryCollection, orderBy('savedAt', 'desc'));
    return collectionData(q);
  }

  /**
   * Directly fetches session document metadata from Firestore to resolve session timestamps.
   */
  async fetchSessionMetadata(machineId: string, sessionId: string, dateHint?: string | null): Promise<any> {
    try {
      if (dateHint) {
        const nestedRef = doc(this.firestore, 'devices', machineId, 'sessions', dateHint, 'sessions', sessionId);
        const nestedSnap = await getDoc(nestedRef);
        if (nestedSnap.exists()) {
          return nestedSnap.data();
        }
      }
      const legacyRef = doc(this.firestore, 'devices', machineId, 'sessions', sessionId);
      const legacySnap = await getDoc(legacyRef);
      if (legacySnap.exists()) {
        return legacySnap.data();
      }
    } catch (e) {
      console.warn('Error fetching session metadata:', e);
    }
    return null;
  }

  /**
   * Generates a user-friendly ticket ID in the format RF-XXX-XXX-X (e.g. RF-834-291-5)
   */
  generateRefundTicketId(): string {
    const p1 = Math.floor(100 + Math.random() * 900);
    const p2 = Math.floor(100 + Math.random() * 900);
    const p3 = Math.floor(Math.random() * 10);
    return `RF-${p1}-${p2}-${p3}`;
  }

  /**
   * Helper to convert a browser File to base64 string
   */
  private async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const res = reader.result as string;
        resolve(res);
      };
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  }

  /**
   * Submits a refund request along with optional photo evidence attachments (KAN-24).
   * Uses the trusted Cloud Function submitRefundRequestWithEvidence to validate, strip metadata,
   * re-encode images to WebP, write private manifests, and create the parent ticket atomically.
   */
  async submitRefundRequestWithEvidence(
    requestData: {
      requestorUid: string;
      requestorEmail: string;
      deviceId: string;
      sessionId?: string | null;
      sessionDate?: string | null;
      approximateTime?: string | null;
      identificationMethod: 'qr_scan' | 'guided_match';
      reasonCategory: string;
      reasonText?: string;
      originalPaymentMethod: string;
      originalPaidAmount: number;
      storeLocation?: string;
      wantsCashRefund?: boolean;
    },
    files: File[] = []
  ): Promise<string> {
    // Prepare evidence payload
    const evidenceFilesPayload: { filename: string; mimeType: string; base64Data: string }[] = [];
    for (const file of files) {
      const base64Data = await this.fileToBase64(file);
      evidenceFilesPayload.push({
        filename: file.name,
        mimeType: file.type,
        base64Data
      });
    }

    try {
      if (this.functions) {
        const callable = httpsCallable<any, { success: boolean; requestId: string; ticketId: string }>(
          this.functions,
          'submitRefundRequestWithEvidence'
        );
        const result = await callable({
          requestData,
          evidenceFiles: evidenceFilesPayload
        });
        if (result.data && (result.data.ticketId || result.data.requestId)) {
          return result.data.ticketId || result.data.requestId;
        }
      }
    } catch (err) {
      console.warn('[UserPortalService] Error in submitRefundRequestWithEvidence callable:', err);
      // If error occurs with evidence attachments, bubble it up so user sees clear feedback
      if (files.length > 0) {
        throw err;
      }
    }

    // Fallback: direct create if no files attached and callable unavailable
    return this.createRefundRequest(requestData);
  }

  /**
   * Fetches secure short-lived view URL for an evidence attachment.
   */
  async getRefundEvidenceUrl(requestId: string, attachmentId: string, type: 'thumbnail' | 'full' = 'full'): Promise<{
    downloadUrl: string;
    expiresAt: string;
    dimensions?: { width: number; height: number };
    sanitizedMimeType?: string;
    byteCount?: number;
  }> {
    if (!this.functions) {
      throw new Error('Firebase Functions service is not initialized.');
    }

    const callable = httpsCallable<{ requestId: string; attachmentId: string; type: string }, any>(
      this.functions,
      'getRefundEvidenceUrl'
    );
    const result = await callable({ requestId, attachmentId, type });
    return result.data;
  }


  /**
   * Stream live evidence manifests for a specific refund request.
   */
  getRefundEvidenceManifests(requestId: string): Observable<any[]> {
    const evidenceCol = collection(this.firestore, 'refund_requests', requestId, 'evidence');
    return collectionData(evidenceCol, { idField: 'id' });
  }

  /**
   * Creates a structured refund request in the refund_requests collection with RF-XXX-XXX-X ID.
   */
  async createRefundRequest(requestData: {
    requestorUid: string;
    requestorEmail: string;
    deviceId: string;
    sessionId?: string | null;
    sessionDate?: string | null;
    approximateTime?: string | null;
    identificationMethod: 'qr_scan' | 'guided_match';
    reasonCategory: string;
    reasonText?: string;
    originalPaymentMethod: string;
    originalPaidAmount: number;
    storeLocation?: string;
    wantsCashRefund?: boolean;
  }): Promise<string> {
    const { doc, setDoc, serverTimestamp } = await import('@angular/fire/firestore');
    const ticketId = this.generateRefundTicketId();
    const docRef = doc(this.firestore, 'refund_requests', ticketId);

    // Invariant: If cash refund is selected, it must automatically enter 'needs_review' status after validation
    const isCashRefund = !!requestData.wantsCashRefund;
    const status = 'needs_review';
    const riskFactors = isCashRefund ? ['cash_refund_requested'] : [];
    const riskScore = isCashRefund ? 100 : (requestData.identificationMethod === 'qr_scan' ? 10 : 50);

    const normalizedEmail = (requestData.requestorEmail || '').trim().toLowerCase();
    await setDoc(docRef, {
      ticketId: ticketId,
      requestorUid: requestData.requestorUid,
      requestorEmail: normalizedEmail,
      deviceId: requestData.deviceId || '',
      sessionId: requestData.sessionId || null,
      sessionDate: requestData.sessionDate || null,
      approximateTime: requestData.approximateTime || null,
      storeLocation: requestData.storeLocation || null,
      identificationMethod: requestData.identificationMethod,
      reasonCategory: requestData.reasonCategory,
      reasonText: requestData.reasonText || '',
      originalPaymentMethod: requestData.originalPaymentMethod || 'Card',
      originalPaidAmount: Number(requestData.originalPaidAmount) || 0,
      eligibleCouponAmount: Number(requestData.originalPaidAmount) || 0,
      wantsCashRefund: isCashRefund,
      status: status,
      riskScore: riskScore,
      riskFactors: riskFactors,
      reviewReason: isCashRefund ? 'Customer requested cash refund (requires manual banking review)' : null,
      hasEvidence: false,
      evidenceCount: 0,
      policyVersion: '1.0.0',
      createdAt: serverTimestamp(),
      requestedAt: serverTimestamp(),
      resolvedAt: null,
      couponCode: null,
      decision: null,
      decisionSource: null,
      decisionRationale: null
    });

    return ticketId;
  }

  /**
   * Stream all refund requests submitted strictly for a specific customer email address.
   */
  getUserRefundRequests(email: string): Observable<any[]> {
    const rawEmail = (email || '').trim();
    const normalizedEmail = rawEmail.toLowerCase();
    if (!normalizedEmail) {
      return of([]);
    }

    const emailVariants = Array.from(new Set([rawEmail, normalizedEmail])).filter(Boolean);
    const refundCol = collection(this.firestore, 'refund_requests');
    const q = emailVariants.length > 1
      ? query(refundCol, where('requestorEmail', 'in', emailVariants))
      : query(refundCol, where('requestorEmail', '==', normalizedEmail));

    return (collectionData(q, { idField: 'id' }) as Observable<any[]>).pipe(
      map(requests => {
        // Enforce strict email matching filter
        return (requests || [])
          .filter(r => {
            const ticketEmail = (r.requestorEmail || '').trim().toLowerCase();
            return ticketEmail === normalizedEmail;
          })
          .sort((a: any, b: any) => {
            const timeA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
            const timeB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
            return timeB - timeA;
          });
      })
    );
  }

  /**
   * Get single coupon details by coupon code.
   */
  getCouponByCode(code: string): Observable<any> {
    const couponRef = doc(this.firestore, 'coupons', code);
    return docData(couponRef, { idField: 'id' });
  }

  /**
   * Stream live status updates for a specific refund request.
   */
  getRefundRequestStream(requestId: string): Observable<any> {
    const docRef = doc(this.firestore, 'refund_requests', requestId);
    return docData(docRef, { idField: 'id' });
  }

  /**
   * Fetches active stores that have photobooth machines mapped to them.
   * Uses a Firestore where('deviceCount', '>', 0) clause to avoid downloading all stores.
   */
  getActiveStoresWithMachines(): Observable<any[]> {
    const storesCol = collection(this.firestore, 'stores');
    const q = query(storesCol, where('deviceCount', '>', 0));

    return (collectionData(q, { idField: 'id' }) as Observable<any[]>).pipe(
      map(stores => {
        return (stores || [])
          .filter(s => !s['isDeleted'])
          .sort((a, b) => {
            const nameA = a.friendlyName || a.friendlyStoreName || a.name || a.storeId || '';
            const nameB = b.friendlyName || b.friendlyStoreName || b.name || b.storeId || '';
            return nameA.localeCompare(nameB);
          });
      })
    );
  }
}